import React, { useState, useEffect, useCallback } from 'react';
import DocsEditor from './docs-editor';
import Toolbox from '../new-toolbox';
import PagesPanel from '../pages-panel';
import {
  Bold,
  Italic,
  Underline,
  Highlighter,
  FileText,
  Heading1,
  Heading2,
  Heading3,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  IndentIncrease,
  IndentDecrease,
  Save,
  Trash2,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import { useToast } from '../../../Utils/toastContext';
import { setCurrentContractId } from '../../../Redux/store/contractSlice';
import { useSelector, useDispatch } from 'react-redux';

import RichTextToolbar from '../rich-text-toolbar';
const BASE_URL = process.env.REACT_APP_BASE_URL;

function IconBtn({ active, onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`
        group relative flex items-center justify-center
        w-9 h-9 rounded-lg transition-all duration-200
        hover:bg-[#375AE7]/8 hover:scale-[1.04]
        active:scale-95
        ${
          active
            ? 'bg-[#375AE7]/12 text-[#375AE7] shadow-sm ring-1 ring-[#375AE7]/20'
            : 'text-slate-500 hover:text-slate-700'
        }
      `}
    >
      <Icon size={16} strokeWidth={active ? 2.5 : 2} />

      <span
        className="
          absolute -bottom-8 left-1/2 -translate-x-1/2 hidden group-hover:block
          text-[10px] font-medium bg-slate-800 text-white px-2.5 py-1 rounded-md
          whitespace-nowrap shadow-lg z-20
        "
      >
        {label}
      </span>
    </button>
  );
}

function TopBar({ editor, data, isEditing, docName, setDocName }) {
  if (!editor) return null;

  return (
    <div
      className="
        sticky top-0 z-10 shrink-0
        flex flex-wrap items-center justify-between gap-3
        px-4 py-2.5 sm:px-5
        bg-white/80 backdrop-blur-xl
        border-b border-slate-200/80
        shadow-[0_1px_12px_rgba(55,90,231,0.04)]
      "
    >
      {/* LEFT - Document identity */}
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#375AE7] text-white shadow-md shadow-[#375AE7]/25">
          <FileText className="h-4 w-4" strokeWidth={2.25} />
        </div>
        <div className="leading-tight min-w-0">
          <input
            value={docName}
            onChange={(e) => setDocName(e.target.value)}
            className="
  truncate text-sm font-semibold text-slate-800
  bg-transparent outline-none
  border-b border-transparent
  hover:border-slate-300 focus:border-[#375AE7]
  focus:ring-0 transition-all
  w-full max-w-[260px]
  px-1 py-0.5 rounded
"
            placeholder="Untitled Document"
          />
        </div>
      </div>

      {/* CENTER - Formatting toolbar */}
      {isEditing && (
        <RichTextToolbar
          editor={editor}
          IconBtn={IconBtn}
          icons={{
            Bold,
            Italic,
            Underline,
            Highlighter,
            Heading1,
            Heading2,
            Heading3,
            AlignLeft,
            AlignCenter,
            AlignRight,
            AlignJustify,
            List,
            ListOrdered,
            IndentIncrease,
            IndentDecrease,
          }}
        />
      )}
      {/* RIGHT - Save */}
    </div>
  );
}
const formatDateTime = (dateTimeString) => {
  const date = new Date(dateTimeString);

  const pad = (n) => String(n).padStart(2, '0');

  const formatted =
    `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

  return formatted;
};
export default function TemplateEditor({
  content,
  onChange,
  isBlank,
  onSave,
  contractName,
  isEdit,
}) {
  const [data, setData] = useState({
    pages: [],
    comments: [],
  });
  const { templateId } = useParams();
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const [editor, setEditor] = useState(null);
  const [activePage, setActivePage] = useState('page-1');
  // const location = useLocation();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contract, setContract] = useState(null);

  const [docName, setDocName] = useState(contractName);
  const [isEditing, setIsEditing] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const currentContractId = useSelector((s) => s.contract.currentContractId);

  // const template =
  //     location.state && location.state.template ? location.state.template : null;

  useEffect(() => {
    if (!data || !data.pages || !data.pages.length) return;
    const exists = data.pages.some((p) => p.id === activePage);
    if (!exists) {
      setActivePage(data.pages[0].id);
    }
  }, [data.pages, activePage]);

  useEffect(() => {
    const handleUpdate = (e) => {
      const pages = e.detail?.pages;
      if (!pages) return;
      setData((prev) => ({ ...prev, pages }));
      if (!pages.some((p) => p.id === activePage) && pages[0])
        setActivePage(pages[0].id);
    };

    window.addEventListener('rp:updatePages', handleUpdate);
    window.addEventListener('rp:addPage', handleUpdate);
    return () => {
      window.removeEventListener('rp:updatePages', handleUpdate);
      window.removeEventListener('rp:addPage', handleUpdate);
    };
  }, [activePage]);

  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const handleSavePublishDocument = useCallback(
    debounce(async (e) => {
      e.preventDefault();
      let toastMessage = null;
      try {
        if (contract.status == 'published') {
          const responseRevise = await postReviseAPI();
          if (responseRevise.status == 200) {
            const response = await updateDocAPI();
            if (response.status == 201 || response.status == 200) {
              const responsePublish = await publishDocAPI(
                response.data.contract.id,
              );
              if (responsePublish.status == 200) {
                showToast(responsePublish.message, 'success');
                navigate('/document-management');
              }
            }
          }
        } else {
          const response = await updateDocAPI();
          if (response.status == 201 || response.status == 200) {
            const responsePublish = await publishDocAPI(
              response.data.contract.id,
            );
            if (responsePublish.status == 200) {
              showToast(responsePublish.message, 'success');
              navigate('/document-management');
            }
          }
        }
      } catch (err) {
        toastMessage = { message: err.message, type: 'error' };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000), // Debounce time
    [data, docName, templateId],
  );
  const getDetails = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/agency/contract-library/${templateId}`,
      );

      if (response.status == 200) {
        const documentContent = response.data.contract.document_content;

        setData({
          ...documentContent,
          comments: documentContent.comments || [],
        });

        setDocName(response.data.contract.title);
        setContract(response.data.contract);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) {
          showToast(response.message, 'error');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const markDeleteAPI = async () => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    let apiUrl = `${BASE_URL}/api/v1/agency/contract-library/${templateId}`;
    let bodyData = {};
    try {
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const markArchievedApi = async () => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    let apiUrl = `${BASE_URL}/api/v1/agency/contract-library/${templateId}/archive`;
    let bodyData = {};
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const handleDeleteContract = async () => {
    let toastMessage = null;
    try {
      const response = await (contract.status == 'published'
        ? markArchievedApi()
        : markDeleteAPI());
      if (response.status == 200) {
        toastMessage = { message: response.message, type: 'success' };
        navigate('/document-management');
      } else {
        toastMessage = { message: response.message, type: 'error' };
      }
    } catch (err) {
      toastMessage = { message: err.message, type: 'error' };
    } finally {
      setLoading(false); // Ensure `loading` is false first
      // Show toast after loading is false
      if (toastMessage) {
        setTimeout(() => {
          showToast(toastMessage.message, toastMessage.type);
        }, 300); // Small delay if needed
      }
    }
  };
  const postReviseAPI = async () => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const apiUrl = `${BASE_URL}/api/v1/agency/contract-library/${templateId}/revise`;
    const bodyData = {};

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));

        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const handleSaveDocument = useCallback(
    debounce(async (e) => {
      e.preventDefault();
      let toastMessage = null;
      try {
        if (contract.status == 'published') {
          const responseRevise = await postReviseAPI();
          if (responseRevise.status === 200) {
            const response = await updateDocAPI();
            if (response.status == 201 || response.status == 200) {
              showToast(response.message, 'success');
              setShowSaveModal(false);
              getDetails();
            }
          }
        } else {
          const response = await updateDocAPI();
          if (response.status == 201 || response.status == 200) {
            showToast(response.message, 'success');
            setShowSaveModal(false);
            getDetails();
          }
        }
      } catch (err) {
        toastMessage = { message: err.message, type: 'error' };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000), // Debounce time
    [data, docName, templateId],
  );
  useEffect(() => {
    if (templateId) {
      getDetails();
    }
  }, [templateId]);
  const publishDocAPI = async (contractId) => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const apiUrl = `${BASE_URL}/api/v1/agency/contract-library/${contractId}/publish`;
    const payload = {
      document_content: {
        pages: data.pages,
      },
      title: docName || 'Untitled Document',
    };
    const bodyData = payload;
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const updateDocAPI = async () => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const apiUrl = `${BASE_URL}/api/v1/agency/contract-library/${templateId}`;
    const payload = {
      document_content: {
        pages: data.pages,
      },
      title: docName || 'Untitled Document',
    };
    const bodyData = payload;
    try {
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const handleUpdateDocument = useCallback(
    debounce(async (e) => {
      e.preventDefault();
      let toastMessage = null;
      try {
        const response = await updateDocAPI();
        if (response.status == 200) {
          showToast(response.message, 'success');
          setShowSaveModal(false);
          setDocName(response.data.contract.title);
          dispatch(setCurrentContractId(response.data.contract.id));
          onSave();
          getDetails();
        } else {
          toastMessage = { message: response.message, type: 'error' };
        }
      } catch (err) {
        toastMessage = { message: err.message, type: 'error' };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000), // Debounce time
    [data, docName, currentContractId],
  );
  return (
    <div className="h-screen rounded-md mr-4 flex bg-gradient-to-br from-slate-100 via-[#F5F7FE]/30 to-slate-100 text-slate-800 overflow-hidden">
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      {/* LEFT - Pages */}
      <div className="hidden md:flex md:w-64 shrink-0 flex-col bg-white border-r border-slate-200/80 shadow-sm">
        <PagesPanel
          pages={(data && data.pages) || []}
          activePage={activePage}
          setActivePage={setActivePage}
          isEdit={isEditing}
        />
      </div>

      {/* CENTER - Document stage */}
      <div className="flex-1 relative overflow-hidden flex flex-col min-w-0">
        {!isEditing && (
          <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-yellow-50 border-b border-amber-200 flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="text-amber-500 text-lg">⚠️</div>

              <div className="text-sm text-amber-800">
                <div className="font-semibold">View-only mode</div>
                <div className="text-xs text-amber-700">
                  This template is locked. You can review content before
                  enabling editing.
                </div>
              </div>
            </div>

            <button
              onClick={() => setIsEditing(true)}
              className="
        px-4 py-2 text-xs font-semibold
        bg-[#375AE7] text-white rounded-lg
        shadow hover:bg-[#2E4BC4]
      "
            >
              Enable Editing
            </button>
          </div>
        )}

        <TopBar
          editor={editor}
          data={data}
          isEditing={isEditing}
          docName={docName}
          setDocName={setDocName}
        />

        <div className="flex-1 overflow-auto px-4 py-6 sm:px-8 sm:py-8 md:px-10 md:py-10 flex justify-center">
          <div className="relative w-full max-w-[950px] mx-auto">
            <DocsEditor
              data={data}
              setData={setData}
              activePage={activePage}
              onEditorReady={setEditor}
              setActivePage={setActivePage}
              isEditing={isEditing}
              savedComments={data.comments || []}
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-3 mt-4 px-4 sm:px-6">
          {/* Delete Document */}
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className="
      inline-flex items-center justify-center gap-1.5
      px-3.5 py-2 text-xs font-semibold
      bg-white text-red-600
      border border-red-200
      rounded-lg
      shadow-sm
      hover:bg-red-50 hover:border-red-300
      active:scale-[0.98]
      transition-all
    "
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete Document
          </button>

          {/* Save Draft */}
          <button
            type="button"
            onClick={(e) => {
              if (docName) {
                const payload = {
                  ...data,
                  pages: data.pages,
                  name: docName || 'Untitled Document',
                };

                localStorage.setItem('contract-doc', JSON.stringify(payload));
                setLoading(true);
                handleSaveDocument(e);
              } else {
                setShowSaveModal(true);
              }
            }}
            className="
      inline-flex items-center justify-center gap-1.5
      px-3.5 py-2 text-xs font-semibold
      bg-[#375AE7] text-white
      rounded-lg
      shadow-md shadow-[#375AE7]/25
      hover:bg-[#2E4BC4]
      active:scale-[0.98]
      transition-all
    "
          >
            <Save className="h-3.5 w-3.5" />
            Save as draft
          </button>

          {/* Save Publish */}
          <button
            type="button"
            onClick={(e) => {
              if (docName) {
                const payload = {
                  ...data,
                  pages: data.pages,
                  name: docName || 'Untitled Document',
                };

                localStorage.setItem('contract-doc', JSON.stringify(payload));
                setLoading(true);
                handleSavePublishDocument(e);
              } else {
                setShowSaveModal(true);
              }
            }}
            className="
      inline-flex items-center justify-center gap-1.5
      px-3.5 py-2 text-xs font-semibold
      bg-[#375AE7] text-white
      rounded-lg
      shadow-md shadow-[#375AE7]/25
      hover:bg-[#2E4BC4]
      active:scale-[0.98]
      transition-all
    "
          >
            <Save className="h-3.5 w-3.5" />
            Save & Publish
          </button>
        </div>
      </div>

      {/* RIGHT - Toolbox */}
      {isEditing && (
        <div className="hidden md:flex md:w-72 shrink-0 flex-col overflow-hidden border-l border-slate-200/80 shadow-sm">
          <Toolbox editor={editor} />
        </div>
      )}
      {showSaveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowSaveModal(false)}
          />

          {/* modal */}
          <div className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl p-6 animate-fadeIn">
            <h2 className="text-lg font-semibold text-slate-800">
              Save Document
            </h2>

            <p className="text-xs text-slate-500 mt-1">
              Enter a name for your document before saving.
            </p>

            <input
              value={docName}
              onChange={(e) => setDocName(e.target.value)}
              placeholder="e.g. Contract Draft v1"
              className="
          mt-4 w-full px-4 py-2.5 text-sm
          border border-slate-200 rounded-xl
          outline-none focus:ring-2 focus:ring-[#375AE7]/30
          focus:border-[#375AE7]
        "
            />

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowSaveModal(false)}
                className="px-4 py-2 text-xs font-medium text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>

              <button
                onClick={(e) => {
                  if (!docName) {
                    showToast('Plaese enter the document name', 'error');
                    return;
                  }
                  const payload = {
                    ...data,
                    pages: data.pages,
                    name: docName || 'Untitled Document',
                  };

                  localStorage.setItem('contract-doc', JSON.stringify(payload));
                  setLoading(true);
                  handleSaveDocument(e);
                }}
                className="
            px-4 py-2 text-xs font-semibold
            bg-[#375AE7] text-white rounded-xl
            hover:bg-[#2E4BC4]
            transition-all cursor-pointer
          "
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeleteModal && (
        <Modal
          title="Delete Contract"
          className="max-w-lg"
          onClose={() => setShowDeleteModal(false)}
        >
          <div className="space-y-6">
            {/* Warning Icon */}
            <div className="flex items-center justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <svg
                  className="h-8 w-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v3m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Content */}
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold text-gray-900">
                Are you sure you want to delete this contract?
              </h3>

              <p className="text-sm text-gray-600">
                This action cannot be undone. The contract will be permanently
                removed.
              </p>

              <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 text-left">
                <p className="text-sm font-medium text-gray-900">
                  {contract?.title}
                </p>

                {contract?.created_at && (
                  <p className="mt-1 text-xs text-gray-500">
                    Created at: {formatDateTime(contract?.created_at)}
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-center gap-3 pt-2">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="
            rounded-xl 
            border 
            border-gray-300 
            px-5 
            py-2.5 
            text-sm 
            font-medium 
            text-gray-700
            hover:bg-gray-50
            transition
          "
              >
                Cancel
              </button>

              <button
                onClick={() => handleDeleteContract()}
                className="
            rounded-xl
            bg-red-600
            px-5
            py-2.5
            text-sm
            font-semibold
            text-white
            shadow-sm
            hover:bg-red-700
            transition
          "
              >
                Delete Contract
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
function Modal({ title, children, onClose, className = '' }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className={`
          bg-white rounded-xl shadow-xl w-full max-w-4xl h-[80vh] flex flex-col
          ${className}
        `}
      >
        {/* Header (fixed) */}
        <div className="flex justify-between items-center p-4 border-b shrink-0">
          <h2 className="font-semibold">{title}</h2>
          <button onClick={onClose}>✕</button>
        </div>

        {/* Body (scrollable) */}
        <div className="p-4 flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
//  <div className="flex items-center gap-2">
//         <button
//           type="button"
//           onClick={() => {
//             localStorage.setItem(
//               "contract-doc",
//               JSON.stringify({
//                 ...data,
//                 pages: data.pages,
//               }),
//             );
//           }}
//           className="
//             inline-flex items-center gap-1.5
//             px-3.5 py-2 text-xs font-semibold
//             bg-[#375AE7] text-white
//             rounded-lg shadow-md shadow-[#375AE7]/25
//             hover:bg-[#2E4BC4] active:scale-[0.98]
//             transition-all
//           "
//         >
//           <Save className="h-3.5 w-3.5" />
//           Save Document
//         </button>
//       </div>
